import React from 'react';
import { LuLoader } from 'react-icons/lu';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MultiCombobox } from '@/components/ui/multi-combobox';
import { useAuthContext } from '@/views/auth/Auth';

import { useAdHocMeetingTeamMembers, useInviteClaireToAdHocMeeting } from './ad-hoc-meeting-queries';

interface AdHocMeetingDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const isValidMeetingUrl = (value: string) => {
    try {
        return new URL(value).protocol === 'https:';
    } catch {
        return false;
    }
};

export const AdHocMeetingDialog = ({ open, onOpenChange }: AdHocMeetingDialogProps) => {
    const { selectedTenants, user } = useAuthContext();
    const [meetingUrl, setMeetingUrl] = React.useState('');
    const [participantGUIDs, setParticipantGUIDs] = React.useState<string[]>([]);
    const [urlError, setUrlError] = React.useState<string>();
    const inviteMutation = useInviteClaireToAdHocMeeting();
    const teamMembersQuery = useAdHocMeetingTeamMembers(
        selectedTenants.map(({ tenantGUID }) => tenantGUID),
        user?.profileGUID,
    );
    const availableParticipants = teamMembersQuery.data.filter(
        (member) => member.internalUserGUID !== user?.profileGUID,
    );
    const availableParticipantGUIDs = availableParticipants.map((member) => member.internalUserGUID);
    const selectedParticipantGUIDs = participantGUIDs.filter((participantGUID) =>
        availableParticipantGUIDs.includes(participantGUID),
    );
    const allParticipantsSelected =
        availableParticipantGUIDs.length > 0 &&
        availableParticipantGUIDs.every((participantGUID) => selectedParticipantGUIDs.includes(participantGUID));
    const someParticipantsSelected = availableParticipantGUIDs.some((participantGUID) =>
        selectedParticipantGUIDs.includes(participantGUID),
    );

    const reset = React.useCallback(() => {
        setMeetingUrl('');
        setParticipantGUIDs([]);
        setUrlError(undefined);
    }, []);

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && !inviteMutation.isPending) {
            reset();
        }
        onOpenChange(nextOpen);
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (!isValidMeetingUrl(meetingUrl)) {
            setUrlError('Paste a valid HTTPS meeting invite link.');
            return;
        }
        if (!user) {
            return;
        }
        setUrlError(undefined);
        inviteMutation.mutate(
            {
                organizerProfileGUID: user.profileGUID,
                tenantGUIDs: selectedTenants.map(({ tenantGUID }) => tenantGUID),
                request: {
                    meetingUrl: meetingUrl.trim(),
                    participantInternalUserGUIDs: selectedParticipantGUIDs,
                },
            },
            {
                onSuccess: () => {
                    reset();
                    onOpenChange(false);
                },
            },
        );
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle>Invite Claire to an ad hoc meeting</DialogTitle>
                    <DialogDescription>
                        Claire will join now and create a shared team-meeting note. Selected team members will get
                        access to the note. No household will be linked.
                    </DialogDescription>
                </DialogHeader>
                <form className="space-y-5" onSubmit={handleSubmit}>
                    <div className="space-y-2">
                        <label className="text-sm font-medium" htmlFor="ad-hoc-meeting-url">
                            Meeting invite link
                        </label>
                        <Input
                            id="ad-hoc-meeting-url"
                            type="url"
                            autoFocus
                            required
                            value={meetingUrl}
                            onChange={(event) => {
                                setMeetingUrl(event.target.value);
                                setUrlError(undefined);
                            }}
                            placeholder="https://claro-advisors.zoom.us/j/…"
                            aria-invalid={!!urlError}
                            aria-describedby="ad-hoc-meeting-url-help ad-hoc-meeting-url-error"
                        />
                        <p id="ad-hoc-meeting-url-help" className="text-muted-foreground text-sm">
                            In Zoom, open <strong>Meeting info</strong> and copy the <strong>Invite link</strong> (the
                            https://… value) — not the Meeting ID or passcode.
                        </p>
                        {urlError && (
                            <p id="ad-hoc-meeting-url-error" className="text-destructive text-sm" role="alert">
                                {urlError}
                            </p>
                        )}
                    </div>
                    <div className="space-y-3">
                        <div>
                            <p className="text-sm font-medium">Team participants</p>
                            <p className="text-muted-foreground text-sm">
                                You are always included. Select the other teammates who are in this meeting.
                            </p>
                        </div>
                        {teamMembersQuery.isPending ? (
                            <div className="text-muted-foreground flex items-center gap-2 text-sm">
                                <LuLoader className="animate-spin" /> Loading team members…
                            </div>
                        ) : teamMembersQuery.isError ? (
                            <p className="text-destructive text-sm" role="alert">
                                Team members could not be loaded. Close the dialog and try again.
                            </p>
                        ) : availableParticipants.length === 0 ? (
                            <p className="text-muted-foreground text-sm">No additional team members are available.</p>
                        ) : (
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        id="ad-hoc-select-all-participants"
                                        checked={
                                            allParticipantsSelected ||
                                            (someParticipantsSelected ? 'indeterminate' : false)
                                        }
                                        onCheckedChange={() =>
                                            setParticipantGUIDs(
                                                allParticipantsSelected ? [] : availableParticipantGUIDs,
                                            )
                                        }
                                    />
                                    <label
                                        className="cursor-pointer text-sm font-medium"
                                        htmlFor="ad-hoc-select-all-participants"
                                    >
                                        Select all team members
                                    </label>
                                </div>
                                {availableParticipants.length > 3 ? (
                                    <MultiCombobox
                                        options={availableParticipants.map((member) => ({
                                            value: member.internalUserGUID,
                                            label: member.name || member.email,
                                        }))}
                                        value={selectedParticipantGUIDs}
                                        onValueChange={setParticipantGUIDs}
                                        placeholder="Select team participants"
                                        noOptionsLabel="No team member found"
                                    />
                                ) : (
                                    <div className="space-y-2">
                                        {availableParticipants.map((member) => {
                                            const checkboxId = `ad-hoc-participant-${member.internalUserGUID}`;
                                            const selected = selectedParticipantGUIDs.includes(member.internalUserGUID);
                                            return (
                                                <div key={member.internalUserGUID} className="flex items-center gap-2">
                                                    <Checkbox
                                                        id={checkboxId}
                                                        checked={selected}
                                                        onCheckedChange={() =>
                                                            setParticipantGUIDs((current) =>
                                                                selected
                                                                    ? current.filter(
                                                                          (participantGUID) =>
                                                                              participantGUID !==
                                                                              member.internalUserGUID,
                                                                      )
                                                                    : [...current, member.internalUserGUID],
                                                            )
                                                        }
                                                    />
                                                    <label className="cursor-pointer text-sm" htmlFor={checkboxId}>
                                                        {member.name || member.email}
                                                        {member.name && member.email && (
                                                            <span className="text-muted-foreground">
                                                                {' '}
                                                                — {member.email}
                                                            </span>
                                                        )}
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            disabled={inviteMutation.isPending}
                            onClick={() => handleOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={
                                inviteMutation.isPending ||
                                !user ||
                                teamMembersQuery.isPending ||
                                teamMembersQuery.isError
                            }
                        >
                            {inviteMutation.isPending && <LuLoader className="animate-spin" />}
                            Invite Claire
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
